"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Container from "@/src/components/ui/Container";

export type TabItem = {
  key: string;
  label: string;
  // content property removed
};

type TabsProps = {
  items: TabItem[];
  defaultActiveKey?: string;
  className?: string;
  mode?: "tabs" | "scroll";
  showMainHeader?: boolean;
  initiallySticky?: boolean;
  stickyHeaderClassName?: string;
};

const Tabs = ({
  items,
  defaultActiveKey,
  className = "",
  mode = "tabs",
  showMainHeader = true,
  initiallySticky = false,
  stickyHeaderClassName = "",
}: TabsProps) => {
  const [activeKey, setActiveKey] = useState(
    defaultActiveKey || (items.length > 0 ? items[0].key : ""),
  );
  // If initiallySticky is true, we start as sticky
  const [isSticky, setIsSticky] = useState(initiallySticky);
  const tabsRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Portal container state
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  // Refs for scroll spy and manual scroll lock
  const isManualScrolling = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Find the portal container in the Navbar
    setPortalContainer(document.getElementById("navbar-sticky-tabs"));
  }, []);

  useEffect(() => {
    // If initiallySticky is true, we don't need the observer to toggle stickiness
    if (initiallySticky) {
      setIsSticky(true);
      return;
    }

    const navHeight = window.innerWidth < 768 ? 68 : 80;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const newIsSticky =
          !entry.isIntersecting && entry.boundingClientRect.top < navHeight;
        setIsSticky(newIsSticky);
        // Event dispatch removed as we are now using Portal to Navbar
      },
      {
        threshold: 0,
        rootMargin: `-${navHeight}px 0px 0px 0px`,
      },
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [initiallySticky]);

  // Scroll Spy Observer
  useEffect(() => {
    if (mode !== "scroll") return;

    const navHeight = window.innerWidth < 768 ? 68 : 80;
    const headerOffset = navHeight + 80;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isManualScrolling.current) return;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveKey(entry.target.id);
          }
        });
      },
      {
        rootMargin: `-${headerOffset}px 0px -70% 0px`,
        threshold: 0,
      },
    );

    items.forEach((item) => {
      const element = document.getElementById(item.key);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [mode, items]);

  // Scroll active tab into view
  useEffect(() => {
    const scrollToActiveTab = (sticky: boolean) => {
      const prefix = sticky ? "sticky-" : "";
      const container = document.getElementById(
        `${prefix}tabs-header-container`,
      );
      const tab = document.getElementById(`${prefix}tab-btn-${activeKey}`);

      if (container && tab) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const scrollLeft =
          container.scrollLeft +
          tabRect.left -
          containerRect.left -
          containerRect.width / 2 +
          tabRect.width / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    };

    scrollToActiveTab(false);
    scrollToActiveTab(true);
  }, [activeKey, isSticky]);

  const handleTabClick = (key: string) => {
    if (mode === "tabs") {
      setActiveKey(key);
      const y =
        (tabsRef.current?.getBoundingClientRect().top || 0) +
        window.scrollY -
        150;
      window.scrollTo({ top: y, behavior: "smooth" });
    } else {
      isManualScrolling.current = true;
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

      setActiveKey(key);
      const element = document.getElementById(key);
      if (element) {
        const navHeight = window.innerWidth < 768 ? 68 : 80;
        const tabsHeight = 80;
        const offset = navHeight + tabsHeight;

        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        // Reset manual scrolling lock after animation
        scrollTimeout.current = setTimeout(() => {
          isManualScrolling.current = false;
        }, 1000);
      }
    }
  };

  const renderTabButtons = (sticky: boolean) => (
    <div
      id={`${sticky ? "sticky-" : ""}tabs-header-container`}
      className={`flex items-center gap-6 overflow-x-auto no-scrollbar ${sticky ? "" : "border-b border-border mb-8"}`}
    >
      {items.map((item) => (
        <button
          key={item.key}
          id={`${sticky ? "sticky-" : ""}tab-btn-${item.key}`}
          onClick={() => handleTabClick(item.key)}
          className={`cursor-pointer py-5 text-nowrap transition-colors border-b-2 px-1 ${
            activeKey === item.key
              ? "border-primary text-primary"
              : "border-transparent hover:border-primary text-text-muted hover:text-primary"
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className={`w-full relative ${className}`} ref={tabsRef}>
      {/* Sentinel for scroll detection */}
      <div
        ref={sentinelRef}
        className="absolute top-0 w-full h-[1px] pointer-events-none opacity-0"
      />

      {/* Sticky Header - Portaled to Navbar if available, otherwise fallback or hidden */}
      {isSticky && portalContainer
        ? createPortal(
            <div
              className={`w-full bg-surface border-t border-border/50 ${stickyHeaderClassName}`}
            >
              <Container>{renderTabButtons(true)}</Container>
            </div>,
            portalContainer,
          )
        : isSticky && !portalContainer
          ? // Fallback if portal not found but sticky is active (rare race condition or different layout)
            // We hide it or render fixed as backup. Given requirement, we prefer Portal.
            // If portal container is missing (e.g. slight delay), it will match on next render.
            null
          : null}

      {/* Main Tabs Header */}
      {showMainHeader && renderTabButtons(false)}
    </div>
  );
};

export default Tabs;
